import { useState } from "react";
import { Handshake, ExternalLink, Copy, Check } from "lucide-react";
import { useLang } from "../i18n";
import { api } from "../lib/api";
import { Reveal } from "../components/Reveal";
import { RichText } from "../components/RichText";

// Traccia i click sul link partner (contatore visibile allo Staff in Admin)
const track = (source) => api.post("/sponsor/click", { source }).catch(() => {});

// Pagina sponsor LagoFast: il testo lo scrive lo Staff dalla sezione Testi sito
// (chiavi lago.text / lago.url / lago.code) e la pagina si aggiorna da sola.
// Il banner e' /lagofast-banner.webp in public — cliccabile verso il link partner.
export default function Lagofast() {
  const { t } = useLang();
  const url = (t("lago.url") || "").trim();
  const code = (t("lago.code") || "").trim();
  const [copied, setCopied] = useState(false);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = code;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <main className="min-h-screen pt-32 pb-20" data-testid="lagofast-page">
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <Reveal>
          <p className="hud-label mb-3">{t("lago.over")}</p>
          <h1 className="font-display text-6xl md:text-7xl uppercase mb-10" data-testid="lagofast-title">
            <span className="text-wardogs">{t("lago.title")}</span>
          </h1>
        </Reveal>

        <div className="grid lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] gap-8 items-start">
          <Reveal>
            {url ? (
              <a href={url} target="_blank" rel="noopener noreferrer" onClick={() => track("banner")} className="block group" data-testid="lagofast-banner-link">
                <img
                  src="/lagofast-banner.webp"
                  alt="LagoFast × WARDOGS — Codice sconto ILMASTRO"
                  data-testid="lagofast-banner"
                  className="w-full chamfer border border-[#1A1A1A] transition-all duration-500 group-hover:border-wardogs/60 group-hover:shadow-[0_0_40px_rgba(255,106,0,0.15)]"
                />
              </a>
            ) : (
              <img
                src="/lagofast-banner.webp"
                alt="LagoFast × WARDOGS — Codice sconto ILMASTRO"
                data-testid="lagofast-banner"
                className="w-full chamfer border border-[#1A1A1A]"
              />
            )}
          </Reveal>

          <div>
            <Reveal>
              <div className="chamfer border border-[#1A1A1A] bg-[#0C0C0C] p-8 mb-6">
                <div className="flex items-center gap-3 mb-5">
                  <Handshake className="w-5 h-5 text-wardogs" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel">WARDOGS Italian Gamer × LagoFast</span>
                </div>
                <RichText tag="div" text={t("lago.text")} className="text-steel text-base leading-relaxed" data-testid="lagofast-text" />
              </div>
            </Reveal>

            {code && (
              <Reveal>
                <div className="chamfer border border-wardogs/40 bg-[#0C0805] p-6 mb-6" data-testid="lagofast-code-card">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-wardogs mb-3" data-testid="lagofast-code-label">
                    {t("lago.code_label")}
                  </p>
                  <div className="flex flex-wrap items-center gap-4">
                    <span className="allow-copy font-display text-4xl md:text-5xl text-white tracking-wide select-text" data-testid="lagofast-code">
                      {code}
                    </span>
                    <button
                      onClick={copyCode}
                      data-testid="lagofast-copy-code-button"
                      className={`chamfer-sm font-mono text-[10px] uppercase tracking-[0.2em] font-bold px-4 py-2.5 transition-colors duration-300 inline-flex items-center gap-2 ${
                        copied ? "bg-green-600 text-white" : "bg-[#1A1A1A] hover:bg-wardogs hover:text-[#050505] text-white"
                      }`}
                    >
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      {copied ? t("lago.copied") : t("lago.copy")}
                    </button>
                  </div>
                </div>
              </Reveal>
            )}

            {url && (
              <Reveal>
                <a href={url} target="_blank" rel="noopener noreferrer" onClick={() => track("cta")}
                  className="chamfer-sm bg-wardogs hover:bg-wardogs-hover text-[#050505] font-mono text-[11px] uppercase tracking-[0.2em] font-bold px-6 py-3.5 transition-colors duration-300 inline-flex items-center gap-2"
                  data-testid="lagofast-cta">
                  {t("lago.cta")} <ExternalLink className="w-4 h-4" />
                </a>
              </Reveal>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
